import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  Platform,
  ScrollView,
  KeyboardAvoidingView,
  Alert,
} from 'react-native';
import { getColor, spacing, getBorderRadius } from '../../theme/useTheme';
import { H1, Body1, Caption, ButtonText } from '../../components/common/TextStyles';
import Slider from '@react-native-community/slider';
import { ColorPicker } from 'react-native-color-picker';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import AdminLayout from '../components/layout/AdminLayout';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';

const InspirationAddScreen = ({ navigation }) => {
  const [verseContent, setVerseContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#6366F1');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [date, setDate] = useState(new Date());

  const handleSubmit = async () => {
    try {
      if (!isFormValid()) {
        Alert.alert('Error', 'Please provide either a verse or a valid video URL (YouTube or Direct MP4)');
        return;
      }

      const hasVerse = verseContent.trim().length > 0;
      const hasVideo = videoUrl.trim().length > 0;
      const currentDate = date.toISOString().split('T')[0];

      const inspirations = [];

      if (hasVerse) {
        inspirations.push({
          content_type: 'verse',
          content: verseContent.trim(),
          background_color: backgroundColor,
          display_date: currentDate
        });
      }

      if (hasVideo) {
        inspirations.push({
          content_type: 'video',
          content: videoUrl.trim(),
          background_color: null,
          display_date: currentDate
        });
      }

      const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
      if (!token) {
        throw new Error('No auth token found. Please login as admin.');
      }

      // Save inspirations one by one
      let successCount = 0;
      for (const inspiration of inspirations) {
        const response = await fetch(`${getApiUrl()}/inspirations/admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(inspiration)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to add ${inspiration.content_type}`);
        }
        successCount++;
      }

      Alert.alert('Success', `Successfully saved ${successCount} inspiration items`);
      navigation.goBack();
    } catch (error) {
      console.error('Error adding inspirations:', error);
      Alert.alert('Error', error.message || 'Failed to add inspirations');
    }
  };

  // Format date for display in the UI
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const onDateChange = (event, selectedDate) => {
    const currentDate = selectedDate || date;
    setShowDatePicker(Platform.OS === 'ios');
    setDate(currentDate);
  };

  const renderDatePicker = () => {
    if (Platform.OS === 'web') {
      return (
        <input
          type="date"
          value={date.toISOString().split('T')[0]}
          onChange={(e) => setDate(new Date(e.target.value))}
          style={{
            padding: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#E5E7EB',
            backgroundColor: '#FFFFFF',
            fontSize: 16,
            width: '100%',
          }}
        />
      );
    }

    return (
      <View>
        <TouchableOpacity
          style={styles.mobileDataSelector}
          onPress={() => setShowDatePicker(true)}
        >
          <Ionicons name="calendar-outline" size={20} color="#6366F1" style={{ marginRight: 10 }} />
          <Text style={styles.dateText}>{formatDateForDisplay(date)}</Text>
        </TouchableOpacity>

        {showDatePicker && (
          <DateTimePicker
            testID="dateTimePicker"
            value={date}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={onDateChange}
          />
        )}
      </View>
    );
  };

  const isFormValid = () => {
    const hasVerse = verseContent.trim().length > 0;
    const hasVideo = videoUrl.trim().length > 0;
    return hasVerse || hasVideo; // Allow either verse or video, or both
  };

  return (
    <AdminLayout>
      <ScrollView style={styles.container}>
        <View style={styles.header}>
          <H1>Add Daily Inspiration</H1>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.closeButton}>
            <Ionicons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <View style={styles.datePickerContainer}>
            <Caption style={styles.label}>Display Date</Caption>
            {renderDatePicker()}
          </View>

          <View style={styles.contentSection}>
            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Verse Content</Caption>
              <TextInput
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={verseContent}
                onChangeText={setVerseContent}
                placeholder="Enter verse or inspirational text..."
                placeholderTextColor={getColor('text.secondary')}
              />
            </View>

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Background Color</Caption>
              <TouchableOpacity
                style={[styles.colorPreview, { backgroundColor }]}
                onPress={() => setShowColorPicker(!showColorPicker)}
              />
            </View>

            {showColorPicker && (
              <View style={styles.colorPicker}>
                <ColorPicker
                  onColorSelected={(color) => {
                    setBackgroundColor(color);
                    setShowColorPicker(false);
                  }}
                  style={{ width: '100%', height: 200 }}
                  defaultColor={backgroundColor}
                  sliderComponent={Slider}
                />
              </View>
            )}

            <View style={[styles.inputGroup, styles.divider]} />

            <View style={styles.inputGroup}>
              <Caption style={styles.label}>Video URL (YouTube or Direct Link)</Caption>
              <TextInput
                style={styles.input}
                value={videoUrl}
                onChangeText={setVideoUrl}
                placeholder="Paste video URL (MP4, YouTube, etc.)..."
                placeholderTextColor={getColor('text.secondary')}
              />
              <Body1 style={styles.helpText}>
                Supports high-quality uploaded videos (Direct MP4 links) and YouTube.
              </Body1>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.submitButton, !isFormValid() && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={!isFormValid()}
          >
            <ButtonText style={styles.submitButtonText}>
              {verseContent && videoUrl
                ? 'Save Both Inspirations'
                : 'Save Inspiration'
              }
            </ButtonText>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing('lg'),
    paddingBottom: spacing('md'),
  },
  content: {
    padding: spacing('lg'),
    paddingTop: 0,
  },
  closeButton: {
    padding: spacing('xs'),
  },
  datePickerContainer: {
    marginBottom: spacing('lg'),
  },
  dateText: {
    fontSize: 16,
    color: getColor('text.primary'),
  },
  contentSection: {
    marginTop: spacing('md'),
  },
  inputGroup: {
    marginBottom: spacing('lg'),
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: getColor('divider'),
    marginVertical: spacing('lg'),
  },
  label: {
    marginBottom: spacing('xs'),
    color: getColor('text.secondary'),
  },
  textArea: {
    borderWidth: 1,
    borderColor: getColor('divider'),
    borderRadius: getBorderRadius('md'),
    padding: spacing('md'),
    minHeight: 120,
    textAlignVertical: 'top',
    color: getColor('text.primary'),
    backgroundColor: getColor('background.paper'),
  },
  input: {
    borderWidth: 1,
    borderColor: getColor('divider'),
    borderRadius: getBorderRadius('md'),
    padding: spacing('md'),
    color: getColor('text.primary'),
    backgroundColor: getColor('background.paper'),
  },
  colorPreview: {
    width: 40,
    height: 40,
    borderRadius: getBorderRadius('sm'),
    borderWidth: 1,
    borderColor: getColor('divider'),
  },
  colorPicker: {
    marginBottom: spacing('md'),
  },
  helpText: {
    marginTop: spacing('xs'),
    color: getColor('text.secondary'),
    fontSize: 12,
  },
  footer: {
    padding: spacing('lg'),
    borderTopWidth: 1,
    borderTopColor: getColor('divider'),
  },
  submitButton: {
    backgroundColor: getColor('primary.main'),
    paddingVertical: spacing('md'),
    borderRadius: getBorderRadius('md'),
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: getColor('action.disabled'),
  },
  submitButtonText: {
    color: getColor('background.default'),
  },
  mobileDataSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: getColor('background.paper'),
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: getColor('divider'),
    padding: spacing('sm'),
    marginTop: spacing('xs'),
  },
  dateAdjustButton: {
    padding: spacing('sm'),
    borderRadius: getBorderRadius('sm'),
  },
  dateDisplay: {
    paddingHorizontal: spacing('lg'),
  },
});

export default InspirationAddScreen;
